import React, { useEffect, useState, useCallback } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from 'react-router-dom';

import Web3 from "web3";
import ApiCall from '../../utils/Api';
import { toast } from 'react-toastify';
import { convertDateFormat, extractTimeFromTimestamp } from "../../utils/services";

export default function FundInvestmentsTable() {
  const contractAddress = import.meta.env.VITE_APP_contractAddress;
  
  const [web3, setWeb3] = useState(null);

  const [dataUnitState, setdataUnitState] = useState([])
  const [groupedInvestments, setGroupedInvestments] = useState([])
  const [ethPrice, setEthPrice] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthPrice(usdPrice);
      } catch (error) {
        // Silent fail for ETH price fetch
      }
    };
    fetchData();
  }, []);
  
  // Function to fetch and process purchase data (using useCallback to maintain reference)
  const getPurchasedDataUnit = useCallback(async () => {
    try {
      const data = await ApiCall.SECURE_API.get(`/buyer/getAllBuyers`)
      setdataUnitState(data.data.data)
      
      // Group purchases by fund (dataUnit) and level
      const purchases = data.data.data || [];
      
        
        const grouped = new Map();
        
        purchases.forEach((purchase, idx) => {
          const dataUnit = purchase.dataUnit;
          // Try multiple ways to get selectedLevel
          const selectedLevel = purchase.selectedLevel || purchase?.selected_level || {};
          
          // Try multiple ways to get levelName
          let levelName = selectedLevel?.levelName || 
                         selectedLevel?.level_name || 
                         selectedLevel?.name ||
                         'Default';
          
          // If still default and we have pricingLevels, try to match by ticketUSD
          if (levelName === 'Default' && dataUnit?.pricingLevels) {
            // Try to match by ticketUSD from selectedLevel
            if (selectedLevel?.ticketUSD) {
              const matchedLevel = dataUnit.pricingLevels.find(level => 
                (level.ticketUSD === selectedLevel.ticketUSD) || 
                (level.ticket_usd === selectedLevel.ticketUSD) ||
                (level.ticketUSD && Math.abs(level.ticketUSD - selectedLevel.ticketUSD) < 0.01)
              );
              if (matchedLevel) {
                levelName = matchedLevel.levelName || matchedLevel.level_name || matchedLevel.name || String(dataUnit.pricingLevels.indexOf(matchedLevel) + 1);
              }
            }
            
            // If still default and we have only one level, use that
            if (levelName === 'Default' && dataUnit.pricingLevels.length === 1) {
              const singleLevel = dataUnit.pricingLevels[0];
              levelName = singleLevel.levelName || singleLevel.level_name || singleLevel.name || '1';
            }
            
            // If still default and multiple levels, try to match by purchase priceWei or totalWei
            if (levelName === 'Default' && purchase.priceWei && web3) {
              try {
                const purchasePriceEth = parseFloat(web3.utils.fromWei(purchase.priceWei, 'ether'));
                if (ethPrice) {
                  const purchasePriceUSD = purchasePriceEth * ethPrice;
                  // Find level that matches this price (within $1 tolerance)
                  const matchedLevel = dataUnit.pricingLevels.find(level => {
                    const levelPrice = level.ticketUSD || level.ticket_usd || 0;
                    return Math.abs(levelPrice - purchasePriceUSD) < 1.0;
                  });
                  if (matchedLevel) {
                    levelName = matchedLevel.levelName || matchedLevel.level_name || matchedLevel.name || String(dataUnit.pricingLevels.indexOf(matchedLevel) + 1);
                  }
                }
              } catch (e) {
                // Silent fail for level price matching
              }
            }
          }
          
          // Final fallback: if still default but we have pricingLevels with indices, use index
          if (levelName === 'Default' && dataUnit?.pricingLevels && dataUnit.pricingLevels.length > 0) {
            // Use first level if we can't determine which one
            levelName = String(1); // Default to Level 1
          }
          
          const fundId = dataUnit?._id?.toString() || 'unknown';
          const key = `${fundId}_${levelName}`;
          
          if (!grouped.has(key)) {
            grouped.set(key, {
              dataUnit: dataUnit,
              level: selectedLevel,
              levelName: levelName,
              purchases: [],
              totalTickets: 0,
              totalInvestmentTDUX: 0,
              totalPurchasePriceUSD: 0,
              firstPurchaseDate: null,
              lastTransactionDate: null
            });
          }
          
          const group = grouped.get(key);
          group.purchases.push(purchase);
          
          // Sum tickets
          const tickets = purchase.ticketsPurchased || 1;
          group.totalTickets += tickets;
          
          // First, get ticketUSD from selectedLevel (from purchase record)
          const ticketUSDFromSelected = selectedLevel?.ticketUSD || selectedLevel?.ticket_usd || 0;
          
          // Find the matching level from pricingLevels to get coinPriceUSD
          let matchedLevel = null;
          if (dataUnit?.pricingLevels && dataUnit.pricingLevels.length > 0) {
            // Try to match by levelName first (with various field name possibilities)
            matchedLevel = dataUnit.pricingLevels.find(level => {
              const levelNameMatch = 
                level.levelName === levelName || 
                level.level_name === levelName ||
                level.name === levelName ||
                String(level.levelName) === String(levelName) ||
                String(level.level_name) === String(levelName) ||
                String(level.name) === String(levelName) ||
                // Also try matching "1" with "Level 1" or vice versa
                (levelName === '1' && (level.levelName === '1' || level.levelName === 'Level 1')) ||
                (levelName === 'Level 1' && (level.levelName === '1' || level.levelName === 'Level 1'));
              return levelNameMatch;
            });
            
            // If not found by name, try to match by ticketUSD from selectedLevel
            if (!matchedLevel && ticketUSDFromSelected > 0) {
              matchedLevel = dataUnit.pricingLevels.find(level => {
                const ticketMatch = 
                  (level.ticketUSD === ticketUSDFromSelected) || 
                  (level.ticket_usd === ticketUSDFromSelected) ||
                  (level.ticketUSD && Math.abs(Number(level.ticketUSD) - Number(ticketUSDFromSelected)) < 0.01);
                return ticketMatch;
              });
            }
            
            // If still not found, try matching by selectedLevel.levelName if it exists
            if (!matchedLevel && selectedLevel?.levelName) {
              matchedLevel = dataUnit.pricingLevels.find(level => {
                return level.levelName === selectedLevel.levelName ||
                       level.level_name === selectedLevel.levelName ||
                       level.name === selectedLevel.levelName ||
                       String(level.levelName) === String(selectedLevel.levelName) ||
                       String(level.level_name) === String(selectedLevel.levelName);
              });
            }
            
            // Fallback to first level if still not found
            if (!matchedLevel) {
              matchedLevel = dataUnit.pricingLevels[0];
            }
          }
          
          // Get ticketUSD (priority: selectedLevel > matchedLevel > fallback)
          const ticketUSD = ticketUSDFromSelected || 
                           matchedLevel?.ticketUSD || 
                           matchedLevel?.ticket_usd || 
                           0;
          
          // Helper function to get coinPrice from any object, checking ALL possible field name variations
          const getCoinPriceUSD = (obj) => {
            if (!obj) return 0;
            
            // List of ALL possible field name variations (case-insensitive check)
            const possibleFieldNames = [
              'coinPriceUSD', 'coin_price_usd', 'coinPrice', 'coin_price',
              'coinPriceUsd', 'coinpriceusd', 'priceUSD', 'price_usd',
              'price', 'coinPriceUSD', 'coinPriceUsd', 'coin_priceUSD'
            ];
            
            // First, try exact matches (most common)
            for (const fieldName of possibleFieldNames) {
              if (obj[fieldName] !== undefined && obj[fieldName] !== null && obj[fieldName] > 0) {
                return Number(obj[fieldName]);
              }
            }
            
            // If not found, check ALL keys in the object (case-insensitive)
            const objKeys = Object.keys(obj);
            for (const key of objKeys) {
              const lowerKey = key.toLowerCase();
              // Check if key contains 'coin' and 'price' or just 'coinprice'
              if ((lowerKey.includes('coin') && lowerKey.includes('price')) || 
                  lowerKey.includes('coinprice') ||
                  (lowerKey === 'price' && obj[key] > 0 && obj[key] < 1000)) { // price field might be coin price if < 1000
                const value = Number(obj[key]);
                if (value > 0) {
                  return value;
                }
              }
            }
            
            return 0;
          };
          
          // Get coinPriceUSD with multiple fallback strategies
          let coinPriceUSD = getCoinPriceUSD(selectedLevel);
          
          // If not in selectedLevel, try matchedLevel
          if (coinPriceUSD === 0 && matchedLevel) {
            coinPriceUSD = getCoinPriceUSD(matchedLevel);
          }
          
          // If still 0, try to find it from any level in pricingLevels that matches ticketUSD
          if (coinPriceUSD === 0 && dataUnit?.pricingLevels && dataUnit.pricingLevels.length > 0 && ticketUSD > 0) {
            const levelWithMatchingTicket = dataUnit.pricingLevels.find(level => {
              const levelTicketUSD = level.ticketUSD || level.ticket_usd || 0;
              const ticketMatch = levelTicketUSD === ticketUSD || 
                                 (levelTicketUSD && Math.abs(Number(levelTicketUSD) - Number(ticketUSD)) < 0.01);
              const hasCoinPrice = getCoinPriceUSD(level) > 0;
              return ticketMatch && hasCoinPrice;
            });
            if (levelWithMatchingTicket) {
              coinPriceUSD = getCoinPriceUSD(levelWithMatchingTicket);
            }
          }
          
          // Last resort: find any level with coinPriceUSD > 0
          if (coinPriceUSD === 0 && dataUnit?.pricingLevels && dataUnit.pricingLevels.length > 0) {
            for (const level of dataUnit.pricingLevels) {
              const price = getCoinPriceUSD(level);
              if (price > 0) {
                coinPriceUSD = price;
                break;
              }
            }
          }
          
          // Calculate investment TDUX: (Ticket USD / Coin Price USD) * Tickets
          // Example: $10 ticket / $0.1 coin price = 100 TDUX per ticket
          let investmentTDUX = 0;
          if (ticketUSD > 0 && coinPriceUSD > 0) {
            // CORRECT calculation: Investment = (Ticket USD / Coin Price USD) * Tickets
            investmentTDUX = (ticketUSD / coinPriceUSD) * tickets;
          } else if (ticketUSD > 0 && coinPriceUSD === 0) {
            // FALLBACK: If coinPriceUSD is missing, use old calculation (ticketUSD as TDUX)
            // This handles old purchases where coinPriceUSD wasn't stored
            investmentTDUX = ticketUSD * tickets; // Old calculation as fallback
          }
          group.totalInvestmentTDUX += investmentTDUX;
          
          // Calculate total purchase price in USD (actual USD purchase price)
          const purchasePriceUSD = ticketUSD * tickets;
          group.totalPurchasePriceUSD += purchasePriceUSD;
          
          // Track first purchase date for yield calculation
          const purchaseDate = purchase.purchaseDate ? new Date(purchase.purchaseDate) : 
                              purchase.createdAt ? new Date(purchase.createdAt) : new Date();
          if (!group.firstPurchaseDate || purchaseDate < group.firstPurchaseDate) {
            group.firstPurchaseDate = purchaseDate;
          }

          // Track last transaction date (most recent purchase)
          if (!group.lastTransactionDate || purchaseDate > group.lastTransactionDate) {
            group.lastTransactionDate = purchaseDate;
          }
        });
        
        // Convert map to array and calculate yield balance
        const groupedArray = Array.from(grouped.values()).map(group => {
          // Find the matching level to get annualYieldPercent
          let matchedLevelForYield = null;
          if (group.dataUnit?.pricingLevels && group.dataUnit.pricingLevels.length > 0) {
            // Try to match by levelName
            matchedLevelForYield = group.dataUnit.pricingLevels.find(level => 
              level.levelName === group.levelName || 
              level.level_name === group.levelName ||
              level.name === group.levelName ||
              String(level.levelName) === String(group.levelName) ||
              String(level.level_name) === String(group.levelName) ||
              String(level.name) === String(group.levelName)
            );
            
            // If not found, try to match by ticketUSD from group.level
            if (!matchedLevelForYield && group.level?.ticketUSD) {
              matchedLevelForYield = group.dataUnit.pricingLevels.find(level => 
                (level.ticketUSD === group.level.ticketUSD) || 
                (level.ticket_usd === group.level.ticketUSD) ||
                (level.ticketUSD && Math.abs(Number(level.ticketUSD) - Number(group.level.ticketUSD)) < 0.01)
              );
            }
            
            // Fallback to first level
            if (!matchedLevelForYield) {
              matchedLevelForYield = group.dataUnit.pricingLevels[0];
            }
          }
          
          // Get annualYieldPercent (priority: group.level > matchedLevel > dataUnit first level)
          const annualYieldPercent = group.level?.annualYieldPercent || 
                                   matchedLevelForYield?.annualYieldPercent || 
                                   group.dataUnit?.pricingLevels?.[0]?.annualYieldPercent || 
                                   0;
          
          let yieldBalance = 0;
          let yieldAddedToBalance = 0;
          
          // If yield is 0%, yield balance should be 0
          if (annualYieldPercent === 0) {
            yieldBalance = 0;
            yieldAddedToBalance = 0;
          } else if (annualYieldPercent > 0 && group.totalInvestmentTDUX > 0 && group.firstPurchaseDate) {
            const currentDate = new Date();
            const purchaseDateObj = new Date(group.firstPurchaseDate);
            
            // Calculate total annual yield in TDUX
            const totalAnnualYield = (group.totalInvestmentTDUX * annualYieldPercent) / 100;
            
            // Calculate months elapsed (full months only, excluding the current month)
            // If purchased on Nov 6, 2025 and today is Nov 15, 2025, monthsElapsed = 0
            // If purchased on Nov 6, 2025 and today is Dec 1, 2025, monthsElapsed = 1
            const monthsElapsed = Math.floor(
              (currentDate.getFullYear() - purchaseDateObj.getFullYear()) * 12 +
              (currentDate.getMonth() - purchaseDateObj.getMonth())
            );
            
            // Monthly payout (1/12th of annual yield)
            const monthlyPayout = totalAnnualYield / 12;
            
            // Yield already paid out (only for completed full months)
            yieldAddedToBalance = monthlyPayout * Math.max(0, monthsElapsed);
            
            // Yield balance = Total annual yield - Yield already paid out
            // Initially (monthsElapsed = 0): yieldBalance = totalAnnualYield
            // After 1 month (monthsElapsed = 1): yieldBalance = totalAnnualYield - monthlyPayout
            yieldBalance = Math.max(0, totalAnnualYield - yieldAddedToBalance);
          }
          
          // Balance = Investment + Yield already added to balance
          const balance = group.totalInvestmentTDUX + yieldAddedToBalance;
          
          return {
            ...group,
            annualYieldPercent,
            yieldBalance,
            yieldAddedToBalance,
            balance,
            totalPurchasePriceUSD: group.totalPurchasePriceUSD,
            lastTransactionDate: group.lastTransactionDate || group.firstPurchaseDate
          };
        });
        
        setGroupedInvestments(groupedArray);
      } catch (error) {
        if (error?.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(error.message);
        }
      }
  }, [web3, ethPrice]);
  
  // Call the function when web3 is available
  useEffect(() => {
    if (web3) {
      getPurchasedDataUnit();
    }
  }, [web3, getPurchasedDataUnit])
  
  // Listen for purchase completion events to refresh data
  useEffect(() => {
    if (!getPurchasedDataUnit) return;
    
    const handlePurchaseComplete = () => {
      // Small delay to ensure backend has saved the purchase
      setTimeout(() => {
        if (web3) {
          getPurchasedDataUnit();
        }
      }, 2000); // 2 second delay to allow backend to save
    };
    
    // Listen for custom event from BuyFundAssestsTable
    window.addEventListener('purchaseCompleted', handlePurchaseComplete);
    
    // Also listen for storage events (in case purchase is saved and storage is updated)
    const handleStorageChange = (e) => {
      if (e.key === 'purchaseCompleted' || e.key === 'fundPurchaseUpdate') {
        handlePurchaseComplete();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Poll for updates every 10 seconds (optional, can be disabled)
    const pollInterval = setInterval(() => {
      if (web3) {
        getPurchasedDataUnit();
      }
    }, 10000); // Refresh every 10 seconds
    
    return () => {
      window.removeEventListener('purchaseCompleted', handlePurchaseComplete);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [web3, getPurchasedDataUnit])
  
  
  useEffect(() => {
    const initWeb3 = async () => {
      if (window.ethereum) {
        const web3Instance = new Web3(window.ethereum);
        try {
          await window.ethereum.enable();
          setWeb3(web3Instance);
        } catch (error) {
          // Silent fail for web3 initialization
        }
      } else if (window.web3) {
        const web3Instance = new Web3(window.web3.currentProvider);
        setWeb3(web3Instance);
      }
    };
    initWeb3();
  }, []);
  
  return (
    <>
      <TableContainer style={{ borderRadius: 9 }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead style={{ backgroundColor: "Black" }}>
            <TableRow>
              <TableCell align="center" style={{ color: "white" }}>Fund Creation Date</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Your Last Transaction Date</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Institution</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Fund</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Level</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Yield</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Tickets</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Investments</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Total Purchase Price</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Yield Balance</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Balance</TableCell>
              <TableCell align="center" style={{ color: "white", maxWidth: 70 }}>Details</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Fund on Explorer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedInvestments && groupedInvestments.length > 0 ? (
              groupedInvestments.map((row, index) => {
                // Get buyer/subscriber ID from the first purchase (all purchases in this group are from the same subscriber)
                const firstPurchase = row.purchases?.[0];
                const buyerId = firstPurchase?.BuyerConsumer?._id || 
                               firstPurchase?.BuyerConsumer || 
                               firstPurchase?.BuyerProvider?._id || 
                               firstPurchase?.BuyerProvider || '';
                
                // Get fund contract address for explorer link
                const fundContractAddr = row.dataUnit?.contractAddress || 
                                        row.dataUnit?.contract_address || 
                                        row.dataUnit?.tokenizedFundAddress ||
                                        contractAddress;
                
                return (
                  <TableRow
                    key={`${row.dataUnit?._id}_${row.levelName}_${index}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">
                      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <span style={{ fontWeight: 600 }}>
                            {convertDateFormat(row.dataUnit?.createdAt)}
                          </span>
                          <span style={{ color: "gray", fontSize: '12px' }}>
                            {extractTimeFromTimestamp(row.dataUnit?.createdAt)}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <span style={{ fontWeight: 600 }}>
                            {row.lastTransactionDate ? convertDateFormat(row.lastTransactionDate) : '-'}
                          </span>
                          <span style={{ color: "gray", fontSize: '12px' }}>
                            {row.lastTransactionDate ? extractTimeFromTimestamp(row.lastTransactionDate) : ''}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell align="center">{row.dataUnit?.provider?.institutionName || 'N/A'}</TableCell>
                    <TableCell align="center">
                      {(() => {
                        // Remove "- Multi-Level Fund" suffix from fund name
                        let fundName = row.dataUnit?.name || 'N/A';
                        fundName = fundName.replace(/\s*-\s*Multi-Level Fund\s*/i, '');
                        
                        // Get contract address for the tokenized fund (try multiple possible fields)
                        const contractAddr = row.dataUnit?.contractAddress || 
                                           row.dataUnit?.contract_address || 
                                           row.dataUnit?.tokenizedFundAddress ||
                                           contractAddress; // Fallback to global contract address
                        
                        // Create explorer link for tokenized fund contract
                        const explorerLink = contractAddr 
                          ? `https://sepolia.etherscan.io/address/${contractAddr}`
                          : null;
                        
                        if (explorerLink) {
                          return (
                            <a
                              href={explorerLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#1976d2',
                                textDecoration: 'none',
                                fontWeight: 500,
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                              {fundName}
                            </a>
                          );
                        }
                        return <span>{fundName}</span>;
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const levelName = row.levelName || row.level?.levelName || row.level?.level_name || row.level?.name;
                        if (levelName && levelName !== 'Default') {
                          // Check if levelName is already a number or contains "Level"
                          if (/^\d+$/.test(levelName)) {
                            return `Level ${levelName}`;
                          } else if (levelName.toLowerCase().includes('level')) {
                            return levelName;
                          } else {
                            return `Level ${levelName}`;
                          }
                        }
                        // Try to get from dataUnit pricingLevels if available
                        if (row.dataUnit?.pricingLevels && row.dataUnit.pricingLevels.length === 1) {
                          const singleLevel = row.dataUnit.pricingLevels[0];
                          const singleLevelName = singleLevel.levelName || singleLevel.level_name || singleLevel.name;
                          return singleLevelName ? `Level ${singleLevelName}` : '-';
                        }
                        return '-';
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      {row.annualYieldPercent ? `${row.annualYieldPercent}%` : '-'}
                    </TableCell>
                    <TableCell align="right">{row.totalTickets || 0}</TableCell>
                    <TableCell align="right">
                      {row.totalInvestmentTDUX && row.totalInvestmentTDUX > 0 
                        ? `${Number(row.totalInvestmentTDUX).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TDUX` 
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {isNaN(row.totalPurchasePriceUSD) || row.totalPurchasePriceUSD === 0 
                        ? '$0.00' 
                        : `$${Number(row.totalPurchasePriceUSD).toFixed(2)}`}
                    </TableCell>
                    <TableCell align="right">
                      {row.yieldBalance !== undefined && row.yieldBalance !== null && row.yieldBalance > 0
                        ? `${Number(row.yieldBalance).toFixed(2)} TDUX` 
                        : row.annualYieldPercent === 0 ? '0.00 TDUX' : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {row.balance && row.balance > 0
                        ? `${Number(row.balance).toFixed(2)} TDUX` 
                        : row.totalInvestmentTDUX && row.totalInvestmentTDUX > 0 ? '0.00 TDUX' : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Link to={`/admin-panel/PurchasedDataUnit/PurchasedDataUnit_Details/${row?.dataUnit?._id}${buyerId ? `/${buyerId}` : ''}`}>
                        Details
                      </Link>
                    </TableCell>
                    <TableCell align="center">
                      {fundContractAddr ? (
                        <a target='_blank' href={`https://sepolia.etherscan.io/address/${fundContractAddr}`}>
                          View on Block Explorer
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}